import { Button, Layout, Checkbox, Loading, Typography } from "matsuri-ui"
import { css } from "@emotion/react"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import styled from "@emotion/styled"
import { PhotoPageLayout } from "../../../templates/PhotoPageLayout"
import { TakePhotoCover } from "@/components/TakePhotoCover/TakePhotoCover"
import { Task } from "@/hooks/useCleaningTasks"
import {
    ImmediatePhotoItem,
    useImmediatePhotoUpload
} from "@/hooks/useImmediatePhotoUpload"
import { assertIsDefined } from "@/utils/assetIsDefined"
import { genCleaningReportPagePath } from "@/routes/protected"
import { theme } from "@/theme"
import { useCleaningInfoDetail } from "@/hooks/useCleaningInfos"
import { BackToDetailConfirmDialog } from "@/components/Dialog/BackToDetailConfirmDialog"
import { useNavigateBackToDetail } from "@/hooks/useNavigateBackToDetail"
import { useTranslation } from "@/hooks/useTranslation"

const CheckListItem = styled.div`
    display: flex;
    align-items: center;
`

const ContentArea = styled.div`
    padding: 10px 0;
`

const SectionTitle = styled.h4`
    margin: 0 0 12px 0;
    font-size: 16px;
`

const PhotosGrid = styled.div`
    display: grid;
    padding: 0 5px;
    grid-template-columns: repeat(auto-fill, minmax(163px, 1fr));
    gap: 8px;
    margin-top: 12px;
`

const PhotoItem = styled.div`
    position: relative;
    width: 163px;
    height: 218px;
`

const PhotoItemImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`

const CheckListSection = ({
    checkList,
    checkedItems,
    onCheckboxChange
}: {
    checkList: string[]
    checkedItems: boolean[]
    onCheckboxChange: (index: number) => void
}) => (
    <ContentArea>
        {checkList.map((item, index) => (
            <CheckListItem key={index}>
                <Checkbox
                    label={item}
                    checked={checkedItems[index] || false}
                    onChange={() => onCheckboxChange(index)}
                />
            </CheckListItem>
        ))}
    </ContentArea>
)

// 撮影直後に状態（アップロード中 / 完了 / 失敗）をサムネイル上に重ねて表示する
const PhotoStatusOverlay = ({
    status,
    onRetry
}: {
    status: ImmediatePhotoItem["status"]
    onRetry: () => void
}) => {
    const { t } = useTranslation()

    if (status === "uploading") {
        return (
            <div
                css={css`
                    position: absolute;
                    inset: 0;
                    display: grid;
                    place-items: center;
                    background-color: rgba(0, 0, 0, 0.35);
                `}
            >
                <Loading
                    css={css`
                        font-size: 28px;
                        color: white;
                    `}
                />
            </div>
        )
    }

    if (status === "putDone") {
        return (
            <div
                css={css`
                    position: absolute;
                    right: 4px;
                    bottom: 4px;
                    display: grid;
                    place-items: center;
                    width: 22px;
                    height: 22px;
                    font-size: 14px;
                    font-weight: bold;
                    color: white;
                    background: #2e9d4e;
                    border-radius: 50%;
                `}
            >
                ✓
            </div>
        )
    }

    // error
    return (
        <button
            onClick={onRetry}
            css={css`
                position: absolute;
                inset: 0;
                display: grid;
                gap: 4px;
                place-items: center;
                font-size: 12px;
                color: white;
                cursor: pointer;
                background-color: rgba(200, 0, 0, 0.55);
                border: none;
            `}
        >
            {t("送信失敗")}
            <span
                css={css`
                    text-decoration: underline;
                `}
            >
                {t("再試行")}
            </span>
        </button>
    )
}

const PhotosSection = ({
    photos,
    onRemovePhoto,
    onRetryPhoto,
    uploadedPhotosLength
}: {
    photos: ImmediatePhotoItem[]
    onRemovePhoto: (id: string) => void
    onRetryPhoto: (id: string) => void
    uploadedPhotosLength: number
}) => {
    const { t } = useTranslation()
    return (
        <ContentArea>
            <SectionTitle>
                {t(`撮影した画像 (${photos.length}枚)`)}
            </SectionTitle>
            <PhotosGrid
                css={
                    uploadedPhotosLength === 0
                        ? css`
                              margin-bottom: 32px;
                          `
                        : undefined
                }
            >
                {photos.map((photo, index) => (
                    <PhotoItem key={photo.id}>
                        <PhotoItemImage
                            src={photo.objectURL}
                            alt={t(`撮影画像 ${index + 1}`)}
                        />
                        <PhotoStatusOverlay
                            status={photo.status}
                            onRetry={() => onRetryPhoto(photo.id)}
                        />
                        <button
                            onClick={() => onRemovePhoto(photo.id)}
                            title={t("写真を削除")}
                            css={css`
                                position: absolute;
                                top: -8px;
                                right: -8px;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                width: 24px;
                                height: 24px;
                                font-size: 16px;
                                font-weight: bold;
                                color: white;
                                cursor: pointer;
                                background: ${
                                    theme.palette.intentions.secondary.main
                                };
                                border: none;
                                border-radius: 50%;
                                &:hover {
                                    background: ${
                                        theme.palette.intentions.secondary.dark
                                    };
                                }
                            `}
                        >
                            ×
                        </button>
                    </PhotoItem>
                ))}
            </PhotosGrid>
        </ContentArea>
    )
}

const UploadedPhotosSection = ({
    uploadedPhotos
}: {
    uploadedPhotos: string[]
}) => {
    const { t } = useTranslation()
    return (
        <ContentArea>
            <SectionTitle>
                {t(`アップロード済みの画像 (${uploadedPhotos.length}枚)`)}
            </SectionTitle>
            <PhotosGrid
                css={css`
                    margin-bottom: 32px;
                `}
            >
                {uploadedPhotos.map((photoUrl, index) => (
                    <PhotoItem key={index}>
                        <PhotoItemImage
                            src={photoUrl}
                            alt={t(`アップロード済み写真 ${index + 1}`)}
                        />
                    </PhotoItem>
                ))}
            </PhotosGrid>
        </ContentArea>
    )
}

// OK 押下後、残りの complete 送信中のみ表示する軽量オーバーレイ（二重操作防止＋フィードバック）
const CompletingOverlay = ({ isVisible }: { isVisible: boolean }) => {
    const { t } = useTranslation()
    if (!isVisible) return null

    return (
        <div
            css={css`
                position: fixed;
                top: 0;
                left: 0;
                display: grid;
                place-items: center;
                width: 100vw;
                height: 100vh;
                height: 100dvh;
                background-color: rgba(0, 0, 0, 0.75);
            `}
        >
            <div
                css={css`
                    display: grid;
                    gap: 16px;
                    place-items: center;
                `}
            >
                <Loading
                    css={css`
                        font-size: 48px;
                    `}
                />
                <Typography
                    css={css`
                        color: white;
                    `}
                >
                    {t("送信しています…")}
                </Typography>
            </div>
        </div>
    )
}

const FooterButtons = ({
    canSkip,
    canProceed,
    isBusy,
    onSkip,
    onNext
}: {
    canSkip: boolean
    canProceed: boolean
    isBusy: boolean
    onSkip: () => Promise<void>
    onNext: () => void
}) => {
    const { t } = useTranslation()
    return (
        <Layout.Column justify="center" gap={1}>
            {!canProceed && canSkip && (
                <div>
                    <Button onClick={onSkip} fullWidth disabled={isBusy}>
                        {t("スキップ")}
                    </Button>
                </div>
            )}
            <div>
                <Button
                    fullWidth
                    onClick={onNext}
                    color="primary"
                    variant="filled"
                    disabled={!canProceed || isBusy}
                >
                    OK
                </Button>
            </div>
        </Layout.Column>
    )
}

export interface TaskProps {
    tasks: Task[]
    handleSkip: (taskIndex: number) => void
    // 撮影と同時に呼ぶ: issue + S3 PUT(pending) のみ。
    requestPutTaskPicture: (
        cleaningId: string,
        taskId: string,
        blob: Blob,
        signal?: AbortSignal
    ) => Promise<{ uploadId: string }>
    // 確定（OK）時に呼ぶ: complete のみ。
    completeTaskPicture: (
        cleaningId: string,
        taskId: string,
        uploadId: string
    ) => Promise<void>
    gotoNextTask: (taskIndex: number) => void
}

export const TaskAssignmentPage = (props: TaskProps) => {
    const { t } = useTranslation()
    const {
        requestPutTaskPicture,
        completeTaskPicture,
        handleSkip,
        gotoNextTask
    } = props
    const { index, cleaningId } = useParams<"index" | "cleaningId">()
    const navigate = useNavigate()

    assertIsDefined(index)
    assertIsDefined(cleaningId)

    const numIndex = useMemo(() => parseInt(index, 10), [index])
    const task = useMemo(() => props.tasks[numIndex], [numIndex, props.tasks])

    // 清掃詳細画面に戻る確認モーダル
    const {
        confirmBackOpen,
        openConfirmBack,
        closeConfirmBack,
        goBackToDetail
    } = useNavigateBackToDetail("cleaning", cleaningId)

    // 全タスク完了時に次のページに遷移
    useEffect(() => {
        if (numIndex && props.tasks.length === numIndex) {
            navigate(genCleaningReportPagePath(cleaningId, "evidences"))
        }
    }, [numIndex, props.tasks.length, cleaningId, navigate])

    // サンプル画像はtaskから派生
    const sampleImage = task?.samplePictureUrl ?? ""
    const [sampleImageLoading, setSampleImageLoading] = useState(true)

    // 撮影と同時アップロード（issue + S3 PUT）を行う即時アップロードフック。
    // complete は OK 押下時にまとめて送る（deferred-complete）。
    const {
        photos,
        addPhoto,
        removePhoto,
        retryPhoto,
        getReadyUploadIds,
        getHasError,
        waitAllSettled,
        reset,
        hasError,
        isUploading,
        allPutDone,
        count
    } = useImmediatePhotoUpload({
        put: (blob, signal) => {
            // task 未確定（初期ロード/範囲外index）のときは PUT を起動しない
            if (!task) {
                return Promise.reject(new Error("task is not ready"))
            }
            return requestPutTaskPicture(cleaningId, task.id, blob, signal)
        }
    })

    // チェックリスト関連の状態
    const [checkedItems, setCheckedItems] = useState<boolean[]>([])

    // OK 押下後の complete 送信中フラグ
    const [completing, setCompleting] = useState(false)

    // タスク変更時のチェックリスト/サンプル画像リセット（render中にprev値と比較する方式）
    const [prevTaskId, setPrevTaskId] = useState<string | undefined>(undefined)
    if (task && task.id !== prevTaskId) {
        setPrevTaskId(task.id)
        setSampleImageLoading(true)
        setCheckedItems(
            task.checkList ? new Array(task.checkList.length).fill(false) : []
        )
        setCompleting(false)
    }

    // タスク切替時に即時アップロード状態をリセット（abort + objectURL revoke は hook 側で実施）。
    // 副作用を伴うため render 中ではなく effect で行う。
    useEffect(() => {
        reset()
    }, [task?.id, reset])

    // 清掃データからアップロード済み写真を派生
    const { token } = useAuth()
    const { data: cleaning, refetch: refetchCleaning } = useCleaningInfoDetail(
        token,
        cleaningId
    )
    const uploadedPhotos = useMemo(() => {
        const pictures = cleaning?.taskResults?.find(
            r => r.taskId === task?.id
        )?.picturesDetail
        return pictures && pictures.length > 0 ? pictures.map(p => p.url) : []
    }, [cleaning?.taskResults, task?.id])

    // イベントハンドラー
    const handleImageLoad = () => setSampleImageLoading(false)

    const handleSkipReport = useCallback(async () => {
        handleSkip(numIndex)
        // スキップ後にデータを再取得してアップロード済み写真を更新
        await refetchCleaning()
    }, [handleSkip, numIndex, refetchCleaning])

    const handleCheckboxChange = (index: number) => {
        setCheckedItems(prev => {
            const newCheckedItems = [...prev]
            newCheckedItems[index] = !newCheckedItems[index]
            return newCheckedItems
        })
    }

    const handleNext = useCallback(async () => {
        // 新しい写真がある場合は OK 時に complete をまとめて送る
        if (count > 0) {
            setCompleting(true)
            // まだ PUT 中のものがあれば完了を待つ（通常は撮影中に終わっている）
            await waitAllSettled()
            // 送信失敗が残っている場合は進ませず、再試行を促す
            if (getHasError()) {
                setCompleting(false)
                return
            }
            try {
                // complete は逐次に送る。バックエンドの complete は
                // task_result(pictures配列)を read-modify-write するため、並列に送ると
                // ロストアップデートで確定が1枚に減る。直列化して全件確定させる。
                const uploadIds = getReadyUploadIds()
                for (const uploadId of uploadIds) {
                    await completeTaskPicture(cleaningId, task.id, uploadId)
                }
                reset()
                // 確定後にデータを再取得
                await refetchCleaning()
            } catch (error) {
                console.error("Upload failed:", error)
                setCompleting(false)
                return
            }
            setCompleting(false)
        }

        // 次のタスクに進む
        gotoNextTask(numIndex)
    }, [
        count,
        waitAllSettled,
        getHasError,
        getReadyUploadIds,
        completeTaskPicture,
        cleaningId,
        task,
        reset,
        refetchCleaning,
        gotoNextTask,
        numIndex
    ])

    // 計算値
    const hasUploadedPhotos = uploadedPhotos.length > 0
    // ローカル写真が無い、または全て PUT 完了済みなら確定可能
    const localReady = count === 0 || allPutDone

    const checkListLength = task?.checkList?.length ?? 0
    const allCheckListItemsChecked = useMemo(() => {
        if (checkListLength === 0) {
            return true // チェックリストがない場合は常にtrue
        }
        return (
            checkedItems.length === checkListLength &&
            checkedItems.every(checked => checked)
        )
    }, [checkListLength, checkedItems])

    // 写真（既存 or 新規）があり、新規はすべて PUT 完了・エラー無し、かつチェックリスト完了で確定可能
    const hasContent = hasUploadedPhotos || count > 0
    const canProceed =
        hasContent && allCheckListItemsChecked && localReady && !hasError

    return (
        <>
            {/* 清掃詳細画面に戻る確認モーダル */}
            <BackToDetailConfirmDialog
                type="cleaning"
                open={confirmBackOpen}
                onClose={closeConfirmBack}
                onConfirm={goBackToDetail}
            />
            {numIndex !== undefined && task ? (
                <PhotoPageLayout
                    headerTitle={t("清掃箇所")}
                    progressBar={{
                        value: numIndex + 1,
                        max: props.tasks.length
                    }}
                    page={{
                        title: t(`${task.title}を撮影してください`),
                        description: task.description
                    }}
                    onNext={openConfirmBack}
                    footer={
                        <FooterButtons
                            canSkip={task.canSkip}
                            canProceed={canProceed}
                            isBusy={isUploading || completing}
                            onSkip={handleSkipReport}
                            onNext={handleNext}
                        />
                    }
                >
                    <div
                        css={css`
                            padding-top: 20px;
                        `}
                    >
                        <Layout.Row gap={2} justify={"center"}>
                            <div>
                                <img
                                    width={163}
                                    height={218}
                                    src={sampleImage}
                                    onLoad={handleImageLoad}
                                    css={css`
                                        opacity: ${
                                            sampleImageLoading ? "0" : "1"
                                        };
                                        object-fit: cover;
                                    `}
                                />
                            </div>
                            <div>
                                <TakePhotoCover onBlob={addPhoto} />
                            </div>
                        </Layout.Row>
                    </div>

                    {task.checkList && task.checkList.length > 0 && (
                        <CheckListSection
                            checkList={task.checkList}
                            checkedItems={checkedItems}
                            onCheckboxChange={handleCheckboxChange}
                        />
                    )}

                    {photos.length > 0 && (
                        <PhotosSection
                            photos={photos}
                            onRemovePhoto={removePhoto}
                            onRetryPhoto={retryPhoto}
                            uploadedPhotosLength={uploadedPhotos.length}
                        />
                    )}

                    {uploadedPhotos.length > 0 && (
                        <UploadedPhotosSection
                            uploadedPhotos={uploadedPhotos}
                        />
                    )}

                    <CompletingOverlay isVisible={completing} />
                </PhotoPageLayout>
            ) : (
                "loading..."
            )}
        </>
    )
}
